import React, { useState, useEffect, useMemo } from 'react';
import { HiMagnifyingGlass, HiListBullet, HiArrowDownTray, HiExclamationTriangle } from 'react-icons/hi2';
import './Mermas.scss';
import { getAllMermasByLocalId } from '../../../../../../crud/management_api/stock_management';
import { Merma, ProductStock, StockShortage } from '../../types';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import CheckboxMultiSelect from '../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import ActiveFilter, { ActiveFilterItem } from '../../../../../../components/common/ActiveFilter/ActiveFilter';
import  MermaDetail  from './components/MermaDetail/MermaDetail';
import { exportStockToExcel } from '../../utils/stockExcelExport';

const Mermas: React.FC = () => {
  const [data, setData] = useState<Merma[]>([]);
  const [filteredData, setFilteredData] = useState<Merma[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStorageUnits, setSelectedStorageUnits] = useState<Array<{ value: string | number; label: string }>>([]);
  const [selectedItems, setSelectedItems] = useState<Array<{ value: string | number; label: string }>>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMerma, setSelectedMerma] = useState<Merma | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Derived filter options from merma data
  const storageOptions = useMemo(() => {
    const uniqueStorages = [...new Set(data.map(item => item.storage_unit_name).filter(Boolean))];
    return uniqueStorages.map(storage => ({ value: storage, label: storage }));
  }, [data]);

  // Set default date range to last 7 days
  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    setStartDate(sevenDaysAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) return;
      
      setIsLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem('user') ?? '{}');
        const localId = user?.local_id;
        if (!localId) {
          console.warn('Local ID no encontrado en localStorage');
          return;
        }
        
        const response = await getAllMermasByLocalId(localId, startDate, endDate);
        console.log('Raw API response:', response.data);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching mermas:', error);
        toast.error('Error al cargar las mermas');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [startDate, endDate]);

  useEffect(() => {
    let filtered = data;

    // Filter by search text
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter(item =>
        (item.ingredient_name && item.ingredient_name.toLowerCase().includes(search)) ||
        (item.storage_unit_name && item.storage_unit_name.toLowerCase().includes(search)) ||
        (item.user_name && item.user_name.toLowerCase().includes(search)) ||
        (item.unidad_medida_acronym && item.unidad_medida_acronym.toLowerCase().includes(search))
      );
    }

    // Filter by storage units
    if (selectedStorageUnits.length > 0) {
      const selectedNames = selectedStorageUnits.map(opt => opt.value);
      filtered = filtered.filter(item => selectedNames.includes(item.storage_unit_name));
    }

    // Filter by ingredients
    if (selectedItems.length > 0) {
      const selectedNames = selectedItems.map(opt => opt.value);
      filtered = filtered.filter(item => 
        selectedNames.includes(item.ingredient_name) ||
        selectedNames.includes(item.unidad_medida_acronym)
      );
    }

    setFilteredData(filtered);
  }, [selectedStorageUnits, selectedItems, data, searchValue]);

  // Active filters for display
  const activeFilters: ActiveFilterItem[] = [
    ...selectedStorageUnits.map(s => ({ label: `Almacén: ${s.label}`, value: `storage_${s.value}` })),
    ...selectedItems.map(i => ({ label: `Ingrediente: ${i.label}`, value: `item_${i.value}` }))
  ];

  const handleRemoveFilter = (value: string) => {
    if (value.startsWith('storage_')) {
      const filterValue = value.replace('storage_', '');
      setSelectedStorageUnits(prev => prev.filter(s => s.value !== filterValue));
    } else if (value.startsWith('item_')) {
      const filterValue = value.replace('item_', '');
      setSelectedItems(prev => prev.filter(i => i.value !== filterValue));
    } else if (value === 'startDate') {
      setStartDate('');
    } else if (value === 'endDate') {
      setEndDate('');
    }
  };

  const formatTimeElapsed = (startDate: string | Date) => {
    const start = new Date(startDate);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h`;
    } else {
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffMinutes}m`;
    }
  };

  const handleViewDetails = (merma: Merma) => {
    setSelectedMerma(merma);
    setShowDetailModal(true);
  };

  // Excel Export Handler
  const handleExportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning('No hay datos de mermas para exportar');
      return;
    }

    try {
      // Export only mermas data, empty arrays for stock and shortages
      const emptyStock: ProductStock[] = [];
      const emptyShortages: StockShortage[] = [];
      
      exportStockToExcel(emptyStock, emptyShortages, filteredData);
      toast.success('Excel exportado exitosamente');
    } catch (error) {
      console.error('Error exporting mermas to Excel:', error);
      toast.error('Error al exportar a Excel. Por favor, intenta nuevamente.');
    }
  };

  const renderMermasList = () => {
    if (filteredData.length === 0) {
      return (
        <div className="mp-empty-state">
          <HiExclamationTriangle className="mp-empty-state-icon" />
          <h3 className="mp-empty-state-title">Sin mermas registradas</h3>
          <p className="mp-empty-state-text">No se han registrado mermas con los filtros aplicados.</p>
        </div>
      );
    }

    return (
      <div className="stock-list-container">
        {filteredData.map((merma, index) => (
          <div key={`${merma.merma_id}`} className="merma-item-row">
            <div className="item-basic-info">
              <span className="item-name">{merma.ingredient_name}</span>
              <div className="item-meta">
                <span className="storage-name">{merma.storage_unit_name}</span>
                <span className="responsible-name">Resp: {merma.user_name}</span>
              </div>
            </div>
            
            <div className="quantity-info">
              <span className="quantity-value">{merma.quantity} {merma.unidad_medida_acronym}</span>
            </div>
            
            <div className="time-info">
              <span className="creation-date">
                {new Date(merma.local_timestamp).toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
              
              <span className="elapsed-time">
                hace {formatTimeElapsed(merma.local_timestamp)}
              </span>
            </div>
            
            <div className="status-actions">
              <button className="mp-toolbar-btn" onClick={() => handleViewDetails(merma)} title="Ver detalles">
                <HiListBullet size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mermas-container">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      
      {/* Unified toolbar: search + filters + actions in one row */}
      <div className="mp-toolbar flex flex-wrap items-center gap-3 mb-3">
        {/* Search */}
        <div className="stock-search-inline">
          <HiMagnifyingGlass className="search-icon" />
          <input
            type="text"
            placeholder="Buscar mermas..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Filter dropdowns */}
        <div className="min-w-[140px]">
          <CheckboxMultiSelect
            options={storageOptions}
            value={selectedStorageUnits}
            onChange={(selected: any) => setSelectedStorageUnits(selected)}
            placeholder="Almacenamiento"
          />
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="stock-toolbar-date"
          />
          <span className="date-separator">–</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="stock-toolbar-date"
          />
        </div>

        {/* Actions — right-aligned */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            className="mp-toolbar-btn"
            onClick={handleExportToExcel}
            disabled={isLoading || filteredData.length === 0}
            title="Exportar mermas a Excel"
          >
            <HiArrowDownTray className="w-4 h-4" />
            Excel
          </button>
        </div>
      </div>

      {/* Active Filters */}
      <ActiveFilter filters={activeFilters} onRemove={handleRemoveFilter} />

      {/* Content */}
      <div className="mermas-content">
        {isLoading ? (
          <LoadingSpinner text="Cargando mermas..." transparent={true} />
        ) : (
          renderMermasList()
        )}
      </div>

      {/* Detail Modal */}
      <MermaDetail
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        merma={selectedMerma}
      />
    </div>
  );
};

export default Mermas;
